/**
 * Club Owner Back-Office - Shelly Button Management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Radio, ChevronLeft, Plus, Settings, Users, MapPin, 
  ShoppingCart, Package, Loader2, Check, AlertTriangle,
  Building2, RefreshCw, Search, X, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Navbar } from '../../components/Navbar';
import ClubNavbar from '../../components/ClubNavbar';
import { useAuthStore } from '../../store';
import api from '../../services/api';
import { toast } from 'sonner';

// Club API endpoints
const clubAPI = {
  getButtons: () => api.get('/club/buttons'),
  getCourts: () => api.get('/club/courts'),
  getPlayers: () => api.get('/club/players'),
  getOrders: () => api.get('/club/orders'),
  purchaseButtons: (data) => api.post('/club/buttons/purchase', data),
  configureButton: (deviceId, data) => api.post(`/club/buttons/${deviceId}/configure`, data),
  registerButton: () => api.post('/club/buttons/register'),
};

const ClubButtons = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuthStore();
  
  // Check if user is club owner or admin
  const isClubOwner = user?.role === 'club_owner' || user?.role === 'admin';
  
  // State
  const [buttons, setButtons] = useState([]);
  const [courts, setCourts] = useState([]);
  const [players, setPlayers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialogs
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedButton, setSelectedButton] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  
  // Purchase form
  const [purchaseQty, setPurchaseQty] = useState(1);
  const [purchaseForm, setPurchaseForm] = useState({
    payer_name: user?.first_name || '',
    payer_surname: user?.last_name || '',
    payer_email: user?.email || '',
    payer_phone: user?.phone || '',
    street_name: '',
    street_number: '',
    apartment: '',
    zip_code: '',
    city: '',
    state: '',
  });
  
  // Config form
  const [configForm, setConfigForm] = useState({
    button_type: 'rental',
    court_id: '',
    player_user_id: '',
  });
  
  // Player search state
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [playerSearchResults, setPlayerSearchResults] = useState([]);
  const [playerSearchLoading, setPlayerSearchLoading] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  
  // Check for purchase result
  useEffect(() => {
    const purchaseResult = searchParams.get('purchase');
    const orderId = searchParams.get('order_id');
    
    if (purchaseResult === 'success') {
      toast.success('¡Compra exitosa! Tu pedido está siendo procesado.');
    } else if (purchaseResult === 'failure') {
      toast.error('El pago no se completó. Intenta de nuevo.');
    } else if (purchaseResult === 'pending') {
      toast.info('Pago pendiente de confirmación.');
    }
  }, [searchParams]);
  
  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [buttonsRes, courtsRes, playersRes, ordersRes] = await Promise.all([
        clubAPI.getButtons(),
        clubAPI.getCourts(),
        clubAPI.getPlayers(),
        clubAPI.getOrders(),
      ]);
      
      setButtons(buttonsRes.data.buttons || []);
      setCourts(courtsRes.data.courts || []);
      setPlayers(playersRes.data.players || []);
      setOrders(ordersRes.data.orders || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos');
      
      if (err.response?.status === 403) {
        toast.error('No tienes permiso para acceder a esta sección');
        navigate('/home');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (!isClubOwner) {
      toast.error('Esta sección es solo para propietarios de club');
      navigate('/home');
      return;
    }
    
    loadData();
  }, [isAuthenticated, isClubOwner, navigate, loadData]);
  
  // Update form when user loads
  useEffect(() => {
    if (user) {
      setPurchaseForm(prev => ({
        ...prev,
        payer_name: user.first_name || prev.payer_name,
        payer_surname: user.last_name || prev.payer_surname,
        payer_email: user.email || prev.payer_email,
        payer_phone: user.phone || prev.payer_phone,
      }));
    }
  }, [user]);
  
  // Handle purchase
  const handlePurchase = async () => {
    const required = ['payer_name', 'payer_surname', 'payer_email', 'payer_phone', 'street_name', 'street_number', 'zip_code', 'city', 'state'];
    const missing = required.filter(f => !purchaseForm[f]);
    
    if (missing.length > 0) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    try {
      setPurchaseLoading(true);
      
      const response = await clubAPI.purchaseButtons({
        quantity: purchaseQty,
        ...purchaseForm,
        shipping_address: {
          street_name: purchaseForm.street_name,
          street_number: purchaseForm.street_number,
          apartment: purchaseForm.apartment,
          zip_code: purchaseForm.zip_code,
          city: purchaseForm.city,
          state: purchaseForm.state,
          country: 'México'
        }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Error al crear la orden');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      toast.error(err.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setPurchaseLoading(false);
    }
  };
  
  // Handle configure button
  const handleConfigure = async () => {
    if (!selectedButton) return;
    
    if (configForm.button_type === 'rental' && !configForm.court_id) {
      toast.error('Selecciona una cancha para la renta');
      return;
    }
    
    if (configForm.button_type === 'resale' && !selectedPlayer) {
      toast.error('Busca y selecciona un jugador para la reventa');
      return;
    }
    
    try {
      setConfigLoading(true);
      
      await clubAPI.configureButton(selectedButton.device_id, {
        button_type: configForm.button_type,
        court_id: configForm.button_type === 'rental' ? configForm.court_id : null,
        player_user_id: configForm.button_type === 'resale' ? selectedPlayer.id : null,
      });
      
      toast.success(`Botón configurado como ${configForm.button_type === 'rental' ? 'renta' : 'reventa'}`);
      setShowConfigDialog(false);
      setSelectedButton(null);
      setSelectedPlayer(null);
      setPlayerSearchQuery('');
      setPlayerSearchResults([]);
      loadData();
    } catch (err) {
      console.error('Config error:', err);
      toast.error(err.response?.data?.detail || 'Error al configurar el botón');
    } finally {
      setConfigLoading(false);
    }
  };
  
  // Search players by email, phone, or name
  const searchPlayers = async (query) => {
    if (!query || query.length < 2) {
      setPlayerSearchResults([]);
      return;
    }
    
    try {
      setPlayerSearchLoading(true);
      const response = await api.get(`/club/search-players?q=${encodeURIComponent(query)}`);
      setPlayerSearchResults(response.data.players || []);
    } catch (err) {
      console.error('Search error:', err);
      setPlayerSearchResults([]);
    } finally {
      setPlayerSearchLoading(false);
    }
  };
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (playerSearchQuery) {
        searchPlayers(playerSearchQuery);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [playerSearchQuery]);
  
  // Open config dialog
  const openConfigDialog = (button) => {
    setSelectedButton(button);
    setConfigForm({
      button_type: button.button_type || 'rental',
      court_id: button.court_id || '',
      player_user_id: button.assigned_user_id || '',
    });
    // Reset player search state
    setPlayerSearchQuery('');
    setPlayerSearchResults([]);
    setSelectedPlayer(button.assigned_user_id ? {
      id: button.assigned_user_id,
      name: button.assigned_user_name,
      email: button.assigned_user_email,
      phone: button.assigned_user_phone,
    } : null);
    setShowConfigDialog(true);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <ClubNavbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }
  
  const unitPrice = 500;
  const totalPrice = unitPrice * purchaseQty;
  
  return (
    <div className="min-h-screen bg-background pb-24">
      <ClubNavbar />
      
      <div className="max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/club')} className="text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Mis Botones Shelly</h1>
              <p className="text-muted-foreground text-sm">Gestión de botones para tu club</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Actualizar
            </Button>
            <Button onClick={() => setShowPurchaseDialog(true)}>
              <ShoppingCart className="w-4 h-4 mr-2" />
              Comprar Botones
            </Button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Radio className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{buttons.length}</p>
                  <p className="text-sm text-muted-foreground">Botones totales</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{buttons.filter(b => b.button_type === 'rental').length}</p>
                  <p className="text-sm text-muted-foreground">Para renta</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{buttons.filter(b => b.button_type === 'resale').length}</p>
                  <p className="text-sm text-muted-foreground">Para reventa</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{buttons.filter(b => !b.button_type).length}</p>
                  <p className="text-sm text-muted-foreground">Sin configurar</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Buttons List */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Radio className="w-5 h-5 text-primary" />
              Botones del Club
            </CardTitle>
            <CardDescription>
              Configura cada botón como renta (asignar a cancha) o reventa (asignar a jugador)
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {buttons.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No tienes botones</h3>
                <p className="text-muted-foreground mb-4">
                  Compra botones Shelly a precio de club ($500 MXN c/u)
                </p>
                <Button onClick={() => setShowPurchaseDialog(true)}>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Comprar Botones
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {buttons.map((button) => (
                  <div key={button.device_id} className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        button.button_type === 'rental' 
                          ? 'bg-blue-500/10' 
                          : button.button_type === 'resale'
                            ? 'bg-green-500/10'
                            : 'bg-yellow-500/10'
                      }`}>
                        <Radio className={`w-6 h-6 ${
                          button.button_type === 'rental' 
                            ? 'text-blue-500' 
                            : button.button_type === 'resale'
                              ? 'text-green-500'
                              : 'text-yellow-500'
                        }`} />
                      </div>
                      
                      <div>
                        <p className="font-medium">{button.device_name || button.device_id}</p>
                        <p className="text-sm text-muted-foreground">
                          {button.button_type === 'rental' ? (
                            <>
                              <MapPin className="w-3 h-3 inline mr-1" />
                              Renta • {button.court_name || 'Sin cancha asignada'}
                              {button.club_name && ` (${button.club_name})`}
                            </>
                          ) : button.button_type === 'resale' ? (
                            <>
                              <Users className="w-3 h-3 inline mr-1" />
                              Reventa • {button.assigned_user_name || 'Sin jugador asignado'}
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              Sin configurar
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {button.button_type && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          button.button_type === 'rental'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {button.button_type === 'rental' ? 'Renta' : 'Reventa'}
                        </span>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openConfigDialog(button)}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Configurar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Recent Orders */}
        {orders.length > 0 && (
          <Card className="bg-card mt-6">
            <CardHeader>
              <CardTitle>Historial de Compras</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {orders.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                    <div>
                      <p className="font-medium">
                        {order.items?.[0]?.quantity || 1} botón(es)
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">${order.total_amount?.toLocaleString()} MXN</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        order.status === 'pagado' ? 'bg-green-500/20 text-green-400' :
                        order.status === 'pendiente' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Purchase Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-lg max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              Comprar Botones Shelly
            </DialogTitle>
            <DialogDescription>
              Precio especial para clubes: <span className="text-primary font-bold">$500 MXN</span> por botón
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Quantity */}
            <div className="p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Cantidad</p>
                  <p className="text-sm text-muted-foreground">Máximo 20 por pedido</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPurchaseQty(q => Math.max(1, q - 1))}
                    disabled={purchaseQty <= 1}
                  >
                    -
                  </Button>
                  <span className="text-xl font-bold w-8 text-center">{purchaseQty}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPurchaseQty(q => Math.min(20, q + 1))}
                    disabled={purchaseQty >= 20}
                  >
                    +
                  </Button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border flex justify-between">
                <span className="text-muted-foreground">Total:</span>
                <span className="text-xl font-bold text-primary">${totalPrice.toLocaleString()} MXN</span>
              </div>
            </div>
            
            {/* Contact info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={purchaseForm.payer_name}
                  onChange={(e) => setPurchaseForm(p => ({ ...p, payer_name: e.target.value }))}
                  placeholder="Juan"
                />
              </div>
              <div>
                <Label>Apellido *</Label>
                <Input
                  value={purchaseForm.payer_surname}
                  onChange={(e) => setPurchaseForm(p => ({ ...p, payer_surname: e.target.value }))}
                  placeholder="García"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={purchaseForm.payer_email}
                  onChange={(e) => setPurchaseForm(p => ({ ...p, payer_email: e.target.value }))}
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div>
                <Label>Teléfono *</Label>
                <Input
                  value={purchaseForm.payer_phone}
                  onChange={(e) => setPurchaseForm(p => ({ ...p, payer_phone: e.target.value }))}
                  placeholder="5512345678"
                />
              </div>
            </div>
            
            {/* Shipping */}
            <div className="pt-2 border-t border-border">
              <p className="text-sm font-medium mb-3">Dirección de envío</p>
              
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="col-span-2">
                  <Label>Calle *</Label>
                  <Input
                    value={purchaseForm.street_name}
                    onChange={(e) => setPurchaseForm(p => ({ ...p, street_name: e.target.value }))}
                    placeholder="Av. Insurgentes"
                  />
                </div>
                <div>
                  <Label>Número *</Label>
                  <Input
                    value={purchaseForm.street_number}
                    onChange={(e) => setPurchaseForm(p => ({ ...p, street_number: e.target.value }))}
                    placeholder="123"
                  />
                </div>
              </div>
              
              <div className="mb-3">
                <Label>Apartamento / Interior</Label>
                <Input
                  value={purchaseForm.apartment}
                  onChange={(e) => setPurchaseForm(p => ({ ...p, apartment: e.target.value }))}
                  placeholder="Opcional"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>C.P. *</Label>
                  <Input
                    value={purchaseForm.zip_code}
                    onChange={(e) => setPurchaseForm(p => ({ ...p, zip_code: e.target.value }))}
                    placeholder="06600"
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label>Ciudad *</Label>
                  <Input
                    value={purchaseForm.city}
                    onChange={(e) => setPurchaseForm(p => ({ ...p, city: e.target.value }))}
                    placeholder="CDMX"
                  />
                </div>
                <div>
                  <Label>Estado *</Label>
                  <Input
                    value={purchaseForm.state}
                    onChange={(e) => setPurchaseForm(p => ({ ...p, state: e.target.value }))}
                    placeholder="CDMX"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handlePurchase}
              disabled={purchaseLoading}
              className="bg-[#009ee3] hover:bg-[#0088cc]"
            >
              {purchaseLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</>
              ) : (
                <>Pagar ${totalPrice.toLocaleString()} MXN</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Configure Button Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Configurar Botón
            </DialogTitle>
            <DialogDescription>
              {selectedButton?.device_name || selectedButton?.device_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Button Type Selection */}
            <div>
              <Label className="mb-3 block">Tipo de configuración</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setConfigForm(f => ({ ...f, button_type: 'rental' }))}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    configForm.button_type === 'rental'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-border hover:border-blue-500/50'
                  }`}
                >
                  <MapPin className={`w-6 h-6 mb-2 ${
                    configForm.button_type === 'rental' ? 'text-blue-500' : 'text-muted-foreground'
                  }`} />
                  <p className="font-medium">Renta</p>
                  <p className="text-xs text-muted-foreground">Asignar a una cancha</p>
                </button>
                
                <button
                  onClick={() => setConfigForm(f => ({ ...f, button_type: 'resale' }))}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    configForm.button_type === 'resale'
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-border hover:border-green-500/50'
                  }`}
                >
                  <Users className={`w-6 h-6 mb-2 ${
                    configForm.button_type === 'resale' ? 'text-green-500' : 'text-muted-foreground'
                  }`} />
                  <p className="font-medium">Reventa</p>
                  <p className="text-xs text-muted-foreground">Asignar a un jugador</p>
                </button>
              </div>
            </div>
            
            {/* Rental: Select Court */}
            {configForm.button_type === 'rental' && (
              <div>
                <Label>Seleccionar cancha</Label>
                <select
                  value={configForm.court_id}
                  onChange={(e) => setConfigForm(f => ({ ...f, court_id: e.target.value }))}
                  className="w-full mt-1 p-2 rounded-md bg-secondary border border-border"
                >
                  <option value="">-- Selecciona una cancha --</option>
                  {courts.map((court) => (
                    <option key={court.court_id} value={court.court_id}>
                      {court.club_name} - {court.court_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Resale: Search Player */}
            {configForm.button_type === 'resale' && (
              <div className="space-y-3">
                <Label>Buscar jugador</Label>
                
                {/* Search input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={playerSearchQuery}
                    onChange={(e) => {
                      setPlayerSearchQuery(e.target.value);
                      if (!e.target.value) {
                        setSelectedPlayer(null);
                      }
                    }}
                    placeholder="Buscar por email, teléfono o nombre..."
                    className="pl-9"
                  />
                  {playerSearchQuery && (
                    <button
                      onClick={() => {
                        setPlayerSearchQuery('');
                        setPlayerSearchResults([]);
                        setSelectedPlayer(null);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Search Results */}
                {playerSearchLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Buscando...
                  </div>
                )}
                
                {!playerSearchLoading && playerSearchResults.length > 0 && !selectedPlayer && (
                  <div className="border border-border rounded-lg max-h-48 overflow-y-auto">
                    {playerSearchResults.map((player) => (
                      <button
                        key={player.id}
                        onClick={() => {
                          setSelectedPlayer(player);
                          setPlayerSearchQuery('');
                          setPlayerSearchResults([]);
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-secondary/50 text-left border-b border-border last:border-0"
                      >
                        <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-green-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {player.first_name} {player.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {player.email || player.phone}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {!playerSearchLoading && playerSearchQuery && playerSearchResults.length === 0 && (
                  <div className="text-sm text-muted-foreground p-3 bg-secondary/30 rounded-lg text-center">
                    No se encontraron jugadores con &quot;{playerSearchQuery}&quot;
                  </div>
                )}
                
                {/* Selected Player Display */}
                {selectedPlayer && (
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-green-400">
                        {selectedPlayer.first_name || selectedPlayer.name} {selectedPlayer.last_name || ''}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {selectedPlayer.email || selectedPlayer.phone}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedPlayer(null)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  Escribe al menos 2 caracteres para buscar
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfigure}
              disabled={configLoading}
            >
              {configLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Guardar configuración</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClubButtons;
